// Exposes structured ContractTemplate objects for the contract management service.
//
// DISABLED 2026-08-02: this module previously inlined the markdown contracts from
// `0. Subpanel/business/contracts/` via import.meta.glob. That is deliberately
// switched off. Do not restore it. Reasons, in order of severity:
//
//   1. WRONG PRICING. Those templates state a Silver setup fee of $2,500 and Gold
//      of $5,000. Exhibit A and the financial model say $24,360, FLAT across both
//      tiers as of 2026-08-27 (was $12,180 Silver / $24,360 Gold). Shipping them
//      risks a contract that understates setup revenue by $21,860 on Silver and
//      $19,360 on Gold.
//   2. UNSUPPORTED CLAIMS. They promise a "Cryptographically Verified Voting
//      System", SOC 2 Type II certification, a 24/7 security operations center,
//      and 99.9%/99.95% uptime guarantees with automatic fee credits. None of
//      that exists. See docs/superpowers/specs/2026-08-02-contract-claims-remediation.md
//   3. THEY ARE NOT THE REAL CONTRACTS. The authoritative Glastonbury agreements
//      live in `0. Subpanel/0. demo_10-28-25_Prep/take-aways_Deliverables-from
//      meeting/Service Agreement (Onboarding|On-Going)/`. Those are correctly
//      priced and already state "Provider does NOT guarantee 99.9% uptime".
//   4. IT NEVER WORKED IN PRODUCTION ANYWAY. `0. Subpanel/` is gitignored, so CI
//      has no such directory and the glob always resolved to ''. Only local dev
//      builds ever inlined the text, which is exactly how a wrong contract could
//      reach a demo unnoticed.
//
// Real contract text must not be inlined into the client bundle. If contract
// generation is built for real, it belongs behind an authenticated server
// endpoint sourcing the November 2025 agreements, not a build-time glob over a
// gitignored folder.
const silverAgreementRaw = '';
const goldAgreementRaw = '';
const customAgreementRaw = '';

import { ContractTemplate, SignatureRequirement } from '@/types/shared/contracts';

interface PackageTemplateSpec {
  id: string;
  name: string;
  tier: 'silver' | 'gold' | 'custom';
  raw: string | undefined;
  description: string;
  legalReviewRequired?: boolean;
}

function buildSignatureRequirements(): SignatureRequirement[] {
  return [
    {
      id: 'sig-req-company-rep',
      role: 'company_representative',
      required: true,
      order: 1,
      signatureType: 'electronic',
    },
    {
      id: 'sig-req-customer',
      role: 'customer',
      required: true,
      order: 2,
      signatureType: 'electronic',
    },
  ];
}

const NOW = new Date().toISOString();

const PACKAGE_SPECS: PackageTemplateSpec[] = [
  {
    id: 'service-agreement-silver-v1',
    name: 'Service Agreement - Silver Package',
    tier: 'silver',
    raw: (silverAgreementRaw as string) || '',
    description: 'Core service agreement for Silver tier (100-500 parcels).',
    legalReviewRequired: true,
  },
  {
    id: 'service-agreement-gold-v1',
    name: 'Service Agreement - Gold Package',
    tier: 'gold',
    raw: (goldAgreementRaw as string) || '',
    description: 'Service agreement for Gold tier (expanded scale & features).',
    legalReviewRequired: true,
  },
  {
    id: 'service-agreement-custom-v1',
    name: 'Service Agreement - Custom Package',
    tier: 'custom',
    raw: (customAgreementRaw as string) || '',
    description: 'Custom/enterprise negotiated service agreement template.',
    legalReviewRequired: true,
  },
];

// Extract a minimal field list by scanning for bracketed placeholders like [CLIENT NAME]
function extractPlaceholders(md: string): string[] {
  const matches = md.match(/\[[A-Z0-9 _-]+\]/g) || [];
  return Array.from(new Set(matches.map((m) => m.slice(1, -1).trim())));
}

export const CONTRACT_TEMPLATES: ContractTemplate[] = PACKAGE_SPECS.map((spec) => {
  const placeholders = extractPlaceholders(spec.raw || '');
  const requiredFields = placeholders.map((ph, idx) => ({
    id: ph.toLowerCase().replace(/[^a-z0-9]+/g, '_'),
    name: ph.toLowerCase().replace(/[^a-z0-9]+/g, '_'),
    label: ph,
    type: 'text' as const,
    validation: { required: true },
    helpText: 'Populated from proposal / customer context where available.',
  }));

  return {
    id: spec.id,
    name: spec.name,
    description: spec.description,
    type: 'service_agreement',
    version: '1.0',
    isActive: true,
    templateContent:
      spec.raw ||
      [
        '# NOT A CONTRACT',
        '',
        'No contract text is available in the application. This placeholder must',
        'never be presented to a client or signed.',
        '',
        'The authoritative service agreements are maintained outside this',
        'application and are not distributed with it. Contact ToolboxLogic for',
        'the current executable agreement.',
      ].join('\n'),
    requiredFields,
    optionalFields: [],
    signatureRequirements: buildSignatureRequirements(),
    legalReviewRequired: !!spec.legalReviewRequired,
    createdAt: NOW,
    updatedAt: NOW,
    createdBy: 'system',
  } as ContractTemplate;
});

export function getTemplateByTier(
  tier: 'silver' | 'gold' | 'custom',
): ContractTemplate | undefined {
  if (tier === 'silver')
    return CONTRACT_TEMPLATES.find((t) => t.id.startsWith('service-agreement-silver'));
  if (tier === 'gold')
    return CONTRACT_TEMPLATES.find((t) => t.id.startsWith('service-agreement-gold'));
  return CONTRACT_TEMPLATES.find((t) => t.id.startsWith('service-agreement-custom'));
}
